/**
 * Single source of truth for the app's horizontal page gutter.
 *
 * Every full-width chrome element that owns the page-edge alignment (the
 * page-content shell, the header bar and sub-nav, the footer, and any
 * page-level shell that wraps a route's body) MUST use `GUTTER_CLASS` for
 * its `mx-auto + max-w-* + px-*` so the left/right edges of all chrome
 * line up at every breakpoint.
 *
 * Progression:
 *   - mobile  (< 640px)  : px-4 = 16 px each side, fills viewport
 *   - tablet  (≥ 640px)  : sm:px-6 = 24 px each side, fills viewport
 *   - desktop (≥ 1024px) : lg:px-8 = 32 px each side, fills until cap
 *   - cap     (≥ 1280px) : container caps at `max-w-7xl` (80 rem / 1280 px)
 *                          and `mx-auto` centers it; interior padding stays
 *                          at 32 px from lg+
 *
 * What this does NOT control (compose alongside, not instead of):
 *   - vertical spacing (`py-*`, `pb-*`)
 *   - flex / grid layout (`flex`, `flex-col`, `gap-*`)
 *   - inner reading-width columns (e.g. `max-w-3xl` for prose) — those are
 *     content concerns and live in the consuming component
 *
 * Drift is blocked by FQ-123 in `scripts/check-frontend-quality.sh`,
 * which bans `mx-auto + max-w-*` page-gutter literals outside this module.
 */
export const GUTTER_CLASS = 'mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8' as const;

/**
 * Narrower variant of `GUTTER_CLASS` for routes whose content reads better
 * inside a tighter column (portfolio, proposal creation). Caps at
 * `max-w-5xl` (64 rem / 1024 px) instead of 7xl (80 rem). Same horizontal
 * padding progression so the left/right edges still align with the header
 * and footer chrome at the breakpoints below the cap.
 *
 * Routes opt in via `NARROW_GUTTER_ROUTES` in `app-shell.tsx`. Same FQ-123
 * drift rules apply — do not inline this elsewhere.
 */
export const GUTTER_CLASS_NARROW = 'mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8' as const;

/**
 * Single source of truth for the app's vertical rhythm between the header
 * and the content (top), and between the content and the footer / mobile
 * nav (bottom). **Every user-facing route uses this one value** — there are
 * deliberately no per-route-group variants, so the top edge of the content
 * never jumps when navigating between screens (e.g. a market feed → a market
 * detail). See MOR-556.
 *
 * Progression:
 *   - mobile  (< 768px)  : pt-0 = flush below the header chrome
 *   - tablet+ (≥ 768px)  : md:pt-8 = 32 px below header
 *   - bottom             : pb-16 = 64 px clearance for the MobileNav floating
 *                          bar (shown until lg)
 *   - desktop (≥ 1024px) : lg:pb-12 = 48 px breathing room above the AppFooter
 *                          (MobileNav hidden at lg+)
 *
 * Applied once on the shell content wrapper (`AppShell`). Page-root wrappers
 * and detail-column wrappers MUST NOT add their own `py-*` / `pt-*` / `pb-*`
 * — that's drift, blocked by FQ-124 in `scripts/check-frontend-quality.sh`.
 */
export const CONTENT_VERTICAL_CLASS = 'pt-0 md:pt-8 pb-16 lg:pb-12' as const;

/**
 * Landing marketing hero bleeds under the fixed slim header (no top inset).
 * The bottom inset lifts the landing footer's copyright clear of the heavily
 * frosted lower portion of the `LandingViewportBlur` (a 128px fade pinned
 * `fixed` to the viewport bottom) at full scroll — its upper band is ~transparent
 * so a partial clearance is enough. See `isSimpleHeaderRoute` in `app-shell.tsx`.
 */
export const LANDING_CONTENT_VERTICAL_CLASS = 'pb-16' as const;

/**
 * Floor for the page-content height. Prevents the footer from creeping up
 * into the viewport on short pages (empty states, very small forms, single-
 * card surfaces).
 *
 * Why a min-height even though `min-h-dvh + flex-1` already expands the
 * content wrapper: in practice the cascade can be defeated by intermediate
 * flex children (e.g. a `display: contents` provider, a short error
 * boundary fallback, or a future layout that breaks the chain). Adding an
 * explicit floor here is defensive — the content area is never smaller
 * than ~60% of the viewport, regardless of what's inside it.
 *
 * Tuned to 60dvh: with header (~64–112 px) + footer (~200 px) chrome on a
 * 800 px viewport that's already ~75% of the viewport for content. 60dvh
 * is the floor for shorter viewports and otherwise lets `flex-1` win.
 */
export const CONTENT_MIN_H_CLASS = 'min-h-[70dvh]' as const;

/**
 * Width of the sticky right sidebar on detail pages (market detail,
 * proposal detail). Hardcoded `w-[380px]` literals previously appeared in
 * the live page, the skeleton, and the route loading.tsx for both market
 * and proposal — five sources of truth for one design value.
 *
 * 380 px is wide enough for the trade form and proposal funding form
 * without crowding the main column. If we ever resize the sidebar (e.g.
 * to fit a richer trade form), change this constant.
 *
 * Drift is blocked by FQ-125 in `scripts/check-frontend-quality.sh`.
 */
export const DETAIL_SIDEBAR_W_CLASS = 'w-[380px]' as const;

/**
 * Reading-column max-widths for inner content surfaces. These constrain
 * prose / forms / narrow content INSIDE the page gutter — they are NOT
 * the page-edge gutter. The gutter is owned by `GUTTER_CLASS`.
 *
 * - `FORM_COLUMN_CLASS` (max-w-2xl, 672 px): gift flows,
 *   onboarding-style narrow content. Preserves a comfortable form width on
 *   desktop where the route does not span a wide column.
 * - `READING_COLUMN_CLASS` (max-w-3xl, 768 px): long-form prose,
 *   editorial guides (learn articles, compare pages, about sections),
 *   FAQ accordions. Wide enough for comfortable line length, narrow
 *   enough to avoid newspaper-column sprawl.
 *
 * Inline `max-w-prose` is still allowed for inline paragraph constraints
 * inside a section (typography concern, not a layout concern).
 */
export const FORM_COLUMN_CLASS = 'max-w-2xl' as const;
export const READING_COLUMN_CLASS = 'max-w-3xl' as const;

/**
 * Vertical rhythm between sections on a page. Two flavours, picked based
 * on content density — pages should pick one and stick to it.
 *
 * - `EDITORIAL_SECTION_RHYTHM` (12 / 14): editorial guides where each
 *   section is dense and visually self-contained (learn, compare,
 *   FAQs, mission/vision blocks).
 * - `LISTING_SECTION_RHYTHM` (2 / 4): tight stack inside dense blocks (inner
 *   {@link ProposalFeed}, sort row→panel). Markets/Launchpad **index** shells
 *   use {@link LISTING_INDEX_PAGE_STACK_CLASS} for title→filters→body gaps.
 *
 * The about / support-mainnet shell uses its own internal rhythm with
 * larger gaps (`space-y-12 md:space-y-20`) because it's marketing copy
 * that needs more visual breathing room — that's encapsulated in
 * `SupportMainnetPageShell`.
 */
export const EDITORIAL_SECTION_RHYTHM = 'space-y-12 md:space-y-14' as const;
export const LISTING_SECTION_RHYTHM = 'space-y-2 sm:space-y-4' as const;

/**
 * Markets + Launchpad **index** page shells (`MarketsPage`, `LaunchpadFeedLayout`,
 * route `loading.tsx` files): `flex flex-col` with **`gap-4` / `md:gap-6`** so
 * the first vertical step is **16px**, not {@link LISTING_SECTION_RHYTHM}'s
 * **`space-y-2` (8px)**, which reads like missing shell content padding.
 * (Markets desktop hero must not use large negative `margin-top` or it
 * cancels that shell padding — see `markets-page.tsx` featured carousel wrappers.)
 */
export const LISTING_INDEX_PAGE_STACK_CLASS = 'flex flex-col gap-4 md:gap-6' as const;

/**
 * Background + dark chrome neutralization for the launchpad proposal feed list {@link Panel}.
 * **Light:** wash only in the **top 35%** (semi-transparent `--gray-100` → solid `--gray-50`).
 * **Dark:** must clear both `background-color` **and** `background-image` — `dark:bg-transparent`
 * alone leaves the light-mode linear-gradient image painted (wrong gray strip). Also strips
 * Panel solid-surface inner shadow + border so the listing matches flat `main`.
 */
export const PROPOSAL_FEED_LIST_PANEL_FILL_CLASS =
  'bg-[linear-gradient(180deg,color-mix(in_oklch,var(--gray-100)_50%,transparent)_0%,var(--gray-50)_35%,var(--gray-50)_100%)] dark:bg-transparent dark:[background-image:none] dark:shadow-none dark:border-transparent' as const;

/**
 * Social leaderboard tabs (`DataTable`) — same shell as the launchpad proposal
 * feed list {@link Panel} (fill + radius + responsive flush chrome on small viewports).
 */
export const SOCIAL_LEADERBOARD_LIST_PANEL_CLASS =
  `${PROPOSAL_FEED_LIST_PANEL_FILL_CLASS} rounded-[calc(var(--radius-card)+0.75rem)] px-3 pb-3 pt-0 max-lg:border-0 max-lg:p-0 max-lg:gap-0 max-lg:shadow-none dark:max-lg:shadow-none` as const;
